import { useState, useCallback } from "react";

import TealiumEnvironment from "types/TealiumEnvironment";

export interface UtagData {
  page_country: string;
  page_language: string;
  tealium_environment: TealiumEnvironment;
  page_url?: string;
  hasSubscription?: boolean;
}

declare global {
  interface Window {
    KAMPYLE_ONSITE_SDK?: {
      loadForm: (formId: string) => void;
      showForm: (formId: string) => void;
    };
    utag?: {
      data: {
        page_country?: string;
        page_language?: string;
        tealium_environment?: string;
        page_url?: string;
        hasSubscription?: boolean;
        [key: string]: unknown;
      };
    };
  }
}

export function useExternalScriptWithData() {
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const injectUtagData = useCallback((data: UtagData) => {
    if (document.getElementById("utag-data")) return;

    const inlineScript = document.createElement("script");
    inlineScript.id = "utag-data";
    inlineScript.type = "text/javascript";
    inlineScript.innerHTML = `
      var utag = {"data": ${JSON.stringify(data)}};
      var utag_data = {"page_url": ${JSON.stringify(data.page_url)}};
    `;

    document.head.appendChild(inlineScript);
  }, []);

  const loadScript = useCallback(
    async (url: string, data: UtagData) => {
      try {
        injectUtagData(data);

        if (document.querySelector(`script[src="${url}"]`)) {
          if (window.KAMPYLE_ONSITE_SDK) {
            setIsSdkLoaded(true);
          }

          return;
        }

        const script = document.createElement("script");
        script.src = url;
        script.async = true;

        script.onload = () => {
          const checkSdkReady = () => {
            if (window.KAMPYLE_ONSITE_SDK) {
              console.log("loaded script");
              setIsSdkLoaded(true);
            } else {
              setTimeout(checkSdkReady, 50);
            }
          };

          checkSdkReady();
        };

        script.onerror = () => {
          setError("Failed to load script");
        };

        document.body.appendChild(script);
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Unknown error");
        }
      }
    },
    [injectUtagData],
  );

  const loadMedalliaForm = useCallback(
    (formId: string): void => {
      if (!isSdkLoaded) {
        console.warn("Loading SDK...");
        return;
      }

      const sdk = window.KAMPYLE_ONSITE_SDK;
      if (!sdk) return;

      sdk.loadForm(formId);

      // "code"-type forms have no built-in onSiteData readiness rule, so loadForm's
      // async fetch of the form markup can still be in flight when showForm runs;
      // wait for its container to exist (capped) instead of calling showForm blindly.
      let attempts = 0;
      const maxAttempts = 40; // ~2s at 50ms intervals
      const waitForFormReady = () => {
        attempts += 1;
        const containerReady = Boolean(document.getElementById("kampyleFormContainer"));
        if (containerReady || attempts >= maxAttempts) {
          console.log("Medallia showForm call:", { formId, containerReady, attempts });
          sdk.showForm(formId);
        } else {
          setTimeout(waitForFormReady, 50);
        }
      };

      waitForFormReady();
    },
    [isSdkLoaded],
  );

  const removeScriptAndCookies = useCallback(() => {
    const expire = (name: string) => {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure`;
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    };

    [
      "kampyle_userid",
      "kampyleSessionPageCounter",
      "kampyleUserPercentile",
      "kampyleUserSession",
      "kampyleUserSessionsCount",
    ].forEach(expire);

    document
      .querySelectorAll<HTMLScriptElement>(
        `script[src^="https://resources.digital-cloud.medallia.eu/wdceu/712393/onsite/"]`,
      )
      .forEach((s) => s.remove());

    document.querySelector(`style[title="MDigital_animationStyle"]`)?.remove();
    document.getElementById("MDigitalLightboxWrapper")?.remove();
    document.getElementById("kampyleFormContainer")?.remove();

    window.KAMPYLE_ONSITE_SDK = undefined;
  }, []);

  return { loadScript, isSdkLoaded, error, loadMedalliaForm, removeScriptAndCookies };
}
